// recovery-ledger-from-journal.ts
//
// Orchestrator recovery (3.40.0): the journaled recovery ops (`operation_intent`
// of kind orchestrator_recovery_ticket / _skipped + their `operation_result`)
// as RecoveryOpRecord[], and the stops still waiting for one. Built from journal
// RECORDS so the writer (tick-start scan) and lock-free readers (`status`)
// derive the same ledger. Malformed / foreign intents are ignored.
import {
  readJournalRecordsLockFree,
  type StopAttemptRecord,
  type StopAttemptScan,
  stopAttemptsFromRecords,
} from "./attempt-finished-journal-reader.js";
import type { JournalRecord } from "./journal-scan-report.js";
import {
  RECOVERY_SKIP_OP_KIND,
  RECOVERY_SKIP_REASONS,
  RECOVERY_TICKET_OP_KIND,
  type RecoveryOpRecord,
  type RecoverySkipReason,
} from "./recovery-guards.js";
import { RECOVERY_TRIGGER_VALUES, type RecoveryTrigger } from "./recovery-trigger-classifier.js";

const OUTCOMES = ["applied", "failed", "skipped"] as const;

function intentRecord(seq: number, delta: Record<string, unknown>): RecoveryOpRecord | undefined {
  const kind = delta.operation_kind;
  if (kind !== RECOVERY_TICKET_OP_KIND && kind !== RECOVERY_SKIP_OP_KIND) return undefined;
  const id = delta.operation_id;
  const p = (delta.params ?? {}) as Record<string, unknown>;
  if (typeof id !== "string" || typeof p.source_ticket !== "string") return undefined;
  // Recovery ops exist only for T1–T3; `other` is never journaled as one.
  if (p.trigger === "other") return undefined;
  if (!(RECOVERY_TRIGGER_VALUES as readonly unknown[]).includes(p.trigger)) return undefined;
  if (typeof p.trigger_seq !== "number") return undefined;
  const reason = (RECOVERY_SKIP_REASONS as readonly unknown[]).includes(p.reason)
    ? (p.reason as RecoverySkipReason)
    : undefined;
  return {
    operation_id: id,
    operation_kind: kind,
    source_ticket: p.source_ticket,
    trigger: p.trigger as RecoveryTrigger,
    trigger_seq: p.trigger_seq,
    ...(typeof p.ticket_id === "string" ? { ticket_id: p.ticket_id } : {}),
    ...(reason === undefined ? {} : { reason }),
    intent_seq: seq,
  };
}

/** Intents in seq order, each with its result (an unknown outcome counts as `failed`). */
export function recoveryLedgerFromRecords(records: readonly JournalRecord[]): RecoveryOpRecord[] {
  const ops = new Map<string, RecoveryOpRecord>();
  for (const { seq, delta } of records) {
    if (delta.kind === "operation_intent") {
      const record = intentRecord(seq, delta);
      if (record !== undefined) ops.set(record.operation_id, record);
    } else if (delta.kind === "operation_result" && typeof delta.operation_id === "string") {
      const op = ops.get(delta.operation_id);
      if (op === undefined) continue;
      const outcome = (OUTCOMES as readonly unknown[]).includes(delta.outcome)
        ? (delta.outcome as RecoveryOpRecord["result"])
        : "failed";
      ops.set(op.operation_id, { ...op, result: outcome, result_seq: seq });
    }
  }
  return [...ops.values()];
}

/** Stops whose ticket is still blocked and whose trigger_seq no op settled (applied/skipped). */
export function findPendingRecoveryTriggers(
  scan: StopAttemptScan,
  ledger: readonly RecoveryOpRecord[],
): StopAttemptRecord[] {
  const settled = new Set(
    ledger
      .filter((r) => r.result === "applied" || r.result === "skipped")
      .map((r) => r.trigger_seq),
  );
  return scan.standing.filter((stop) => !settled.has(stop.seq));
}

export interface RecoveryJournalView {
  scan: StopAttemptScan;
  ledger: RecoveryOpRecord[];
}

/** One lock-free journal read for both views. */
export async function readRecoveryJournal(controlRoot: string): Promise<RecoveryJournalView> {
  const records = await readJournalRecordsLockFree(controlRoot);
  return { scan: stopAttemptsFromRecords(records), ledger: recoveryLedgerFromRecords(records) };
}
